const TOKEN_KEY = 'accessToken';

let cachedToken: string | null = null;

export function getAccessToken(): string | null {
    if (cachedToken) return cachedToken;

    try {
        const storedLocal = localStorage.getItem(TOKEN_KEY);
        if (storedLocal) {
            cachedToken = storedLocal;
            return cachedToken;
        }

        const storedSession = sessionStorage.getItem(TOKEN_KEY);
        if (storedSession) {
            cachedToken = storedSession;
            return cachedToken;
        }

        return null;
    } catch {
        return null;
    }
}

export function setAccessToken(token: string, rememberMe?: boolean): void {
    let useSession = false;

    try {
        useSession =
            rememberMe === false ||
            (rememberMe === undefined && !!sessionStorage.getItem(TOKEN_KEY));
    } catch {
        // Ignore errors accessing storage
    }

    // Always clear existing tokens to prevent stale states or inconsistencies
    clearAccessToken();

    cachedToken = token;
    try {
        if (useSession) {
            sessionStorage.setItem(TOKEN_KEY, token);
        } else {
            localStorage.setItem(TOKEN_KEY, token);
        }
    } catch {
        // storage unavailable — token lives in memory only
    }
}

export function clearAccessToken(): void {
    cachedToken = null;
    try {
        localStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
    } catch {
        // noop
    }
}

export interface IAccessTokenScope {
    readonly userId: string;
    readonly organizationId: string;
}

/**
 * The user and organization the current access token was issued for, read from
 * its payload WITHOUT verification. Only for keying per-tenant client-side state
 * (a form draft, say) — never for an access decision; the server verifies the
 * token on every request. Null when there is no token or it cannot be read.
 */
export function getAccessTokenScope(): IAccessTokenScope | null {
    const payloadPart = getAccessToken()?.split('.')[1];
    if (!payloadPart) return null;
    try {
        // base64url → base64; `atob` tolerates the missing padding.
        const payload: unknown = JSON.parse(
            atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')),
        );
        if (typeof payload !== 'object' || payload === null) return null;
        const { userId, organizationId } = payload as Record<string, unknown>;
        return typeof userId === 'string' && typeof organizationId === 'string'
            ? { userId, organizationId }
            : null;
    } catch {
        return null;
    }
}
